/**
 * Project Execution & Project Management — its own API client.
 *
 * ITS OWN, for the reason Inventory Registry's is its own: products share no code between
 * their folders (CLAUDE.md §1.7).
 *
 * The request shape matches the platform's: same-origin credentials, JSON in and out, and
 * an error whose message is the server's `error` field so a ported validation string —
 * "This program has no opportunities with execution data yet" — reaches the screen intact
 * rather than being flattened to a status line.
 *
 * The ported handlers return the source's `{ success, ... }` envelope, because the source's
 * SCREENS branch on `success`. Preserving it means the cloned client logic needs no edit.
 */

const BASE = '/api/project_management';

export class PmApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'PmApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = (await response.json()) as { error?: string; message?: string };
      message = body.error ?? body.message ?? message;
    } catch {
      // Non-JSON body — keep the status line.
    }
    throw new PmApiError(message, response.status);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const pmGet = <T>(path: string) => request<T>(path);
export const pmPost = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) });

/* ------------------------------------------------------------------ *
 * Row shapes, deliberately loose where the source's own shape is loose.
 * ------------------------------------------------------------------ */

/** A delivery project, as every project selector on these screens renders it. */
export interface PmProject {
  delivery_project_id: string;
  description: string | null;
  methodology: string | null;
}

/**
 * The label for a project in a pulldown.
 *
 * A native <select> sizes itself to its WIDEST OPTION, and these descriptions run to several
 * hundred characters — so an untruncated label makes the control, and its open list, run off
 * the screen entirely. That bug appeared independently on Project Visibility and Screen
 * Permissions, which is why this lives here rather than being fixed per screen.
 *
 * The id always shows in full: it is what identifies the project. Only the description is
 * capped, because it is context.
 */
export function projectOptionLabel(p: PmProject, maxDescription = 52): string {
  const d = (p.description ?? '').trim();
  const short = d.length > maxDescription ? `${d.slice(0, maxDescription).trimEnd()}…` : d;
  return (
    p.delivery_project_id + (short ? ` — ${short}` : '') + (p.methodology ? ` (${p.methodology})` : '')
  );
}

/** One phase row on a Budget or Resource plan: proposed, plus the optional override. */
export interface PmPhaseRow {
  phase: string;
  label: string;
  proposed: number;
  /** `null` means "use proposed" — meaningfully different from 0, which means zero. */
  override: number | null;
}

export interface PmScopePhases {
  phases: PmPhaseRow[];
}

export interface PmProgramOpportunity {
  opportunity_id: string;
  name: string;
  approved?: { build_cost: number; tco3: number } | null;
  phases: PmPhaseRow[];
}

export interface PmProgramPlan {
  success: boolean;
  error?: string;
  project: PmProject;
  phases: { key: string; label: string }[];
  shared: PmScopePhases;
  opportunities: PmProgramOpportunity[];
}

/**
 * One phase row on a Tracker: what was planned, what a human typed, and what the ledger says.
 *
 * `manual` and `paid` are kept SEPARATE rather than collapsed into `actual`, because the
 * screen shows both — the typed number in the input, the ledger total as its placeholder —
 * and the precedence between them is what the Variance column is explaining.
 */
export interface PmTrackerPhaseRow {
  phase: string;
  label: string;
  planned: number;
  /** What a human typed. `null` falls back to the payment/effort ledger. */
  manual: number | null;
  paid?: number;
  has_payments?: boolean;
  actual?: number;
  /* Resource Tracker only. */
  planned_fte?: number;
  working_days?: number;
  planned_effort?: number;
  logged_effort?: number;
  has_log?: boolean;
  actual_effort?: number;
  start?: string;
  end?: string;
}

export interface PmTrackerOpportunity {
  opportunity_id: string;
  name: string;
  phases: PmTrackerPhaseRow[];
}

export interface PmPayment {
  id: number;
  opportunity_id: string | null;
  phase: string;
  vendor: string | null;
  amount: number;
  invoice_number: string | null;
  payment_date: string | null;
  note?: string | null;
}

export interface PmBudgetTracker {
  success: boolean;
  error?: string;
  project: PmProject;
  phases: { key: string; label: string }[];
  shared: { phases: PmTrackerPhaseRow[] };
  opportunities: PmTrackerOpportunity[];
  payments: PmPayment[];
  vendors: string[];
}
